import { useCallback, useEffect, useReducer, useRef } from "react";

type Reducer<S, A> = (state: S, action: A) => S;
type ThunkDispatch<S, A> = (action: ThunkAction<S, A>) => void;
type ThunkAction<S, A> =
  | A
  | ((dispatch: ThunkDispatch<S, A>, getState: () => S) => void);

const isThunk = <S, A>(
  action: ThunkAction<S, A>,
): action is (dispatch: ThunkDispatch<S, A>, getState: () => S) => void =>
  typeof action === "function";

export const useReducerWithThunk = <S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
): [S, ThunkDispatch<S, A>] => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const getStateRef = useRef(() => state);

  useEffect(() => {
    getStateRef.current = () => state;
  }, [state]);

  const customDispatch = useCallback((action: ThunkAction<S, A>) => {
    if (isThunk(action)) {
      action(customDispatch, getStateRef.current);
    } else {
      dispatch(action);
    }
  }, []);

  return [state, customDispatch];
};
